import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Run comparison — FW-CRAWL-DELTA-01 phase 3.
 * Backend: `GET /api/v1/urls/:urlId/run-comparison`.
 *
 * These types mirror `backend/src/utils/crawl-run-diff.ts`. The important one is
 * `state`: a metric only carries a real number when it is `compared`, and the
 * UI must never render `change` without reading it — a capture that failed and a
 * page that did not move look identical otherwise, which is the failure mode the
 * whole feature is built around (MONITORING_CRAWL_DELTAS.md §4).
 */
export type RunComparisonState = 'compared' | 'first-run' | 'not-compared' | 'absent' | 'incomparable';

export type RunComparisonMetricGroup = 'demand' | 'serp' | 'technical' | 'freshness';

export interface CrawlRunMetricDelta {
  key: string;
  label: string;
  group: RunComparisonMetricGroup;
  kind: 'number' | 'date' | 'identity';
  polarity: 'higher-is-better' | 'lower-is-better' | 'neutral';
  state: RunComparisonState;
  reason: string | null;
  current: number | string | null;
  previous: number | string | null;
  change: number | null;
  changedDays: number | null;
  changed: boolean | null;
  direction: 'up' | 'down' | 'flat' | null;
  improved: boolean | null;
  /** The canonical rendering the backend owns — `22 (+15)`, or honest prose. */
  display: string;
}

export interface CrawlRunRef {
  crawlRunId: string;
  previousCrawlRunId: string | null;
  scopeDomain?: string;
  crawlMode?: 'sitemap' | 'curated' | 'domain' | null;
  startedAt?: string | null;
  completedAt?: string | null;
  urlsCaptured?: number | null;
  urlsFailed?: number | null;
  /** `DomainCrawl.coverage`. Null on a run whose source document carried none —
   *  unknown, not zero. A run that analysed fewer URLs than it expected makes
   *  its unreached URLs look like pages that disappeared (phase 5). */
  coverageExpected?: number | null;
  coverageAnalyzed?: number | null;
  cost?: number | null;
  durationMs?: number | null;
  dataWatermark?: string | null;
  backfilled?: boolean;
  captureTrigger?: string | null;
}

/**
 * Which issues resolved and which are new — FW-CRAWL-DELTA-01 phase 4.
 * Backend: `backend/src/utils/crawl-run-issue-diff.ts`.
 *
 * `state` matters here even more than on a metric. An issue list that was never
 * captured is an empty array, and an empty array rendered without reading
 * `state` says *every issue on this page was resolved* — the most flattering
 * possible lie, on the one signal an operator acts on. The lists are always
 * empty unless `state === 'compared'`; render `display` otherwise.
 */
export type CrawlRunIssueSeverity = 'critical' | 'warning';

export interface CrawlRunIssueEntry {
  fingerprint: string;
  severity: CrawlRunIssueSeverity;
  /** Null when the run stored fingerprints without the strings behind them:
   *  the issue is real and cannot be named. */
  label: string | null;
  /** Set on a persisting issue whose wording moved — "(2 scripts)" → "(3 scripts)". */
  previousLabel?: string | null;
}

export interface CrawlRunIssueReclassification {
  fingerprint: string;
  previousFingerprint: string;
  from: CrawlRunIssueSeverity;
  to: CrawlRunIssueSeverity;
  label: string | null;
}

export interface CrawlRunIssueComparison {
  state: RunComparisonState;
  reason: string | null;
  resolved: CrawlRunIssueEntry[];
  /** §13.2's "newly appeared". Not `new`, which is what phase 3 calls a URL
   *  that did not exist last run. */
  appeared: CrawlRunIssueEntry[];
  persisting: CrawlRunIssueEntry[];
  reclassified: CrawlRunIssueReclassification[];
  resolvedCount: number;
  appearedCount: number;
  persistingCount: number;
  reclassifiedCount: number;
  previousCount: number | null;
  currentCount: number | null;
  display: string;
}

export interface CrawlRunComparison {
  urlId: string | null;
  url: string | null;
  state: RunComparisonState;
  reason: string | null;
  absentSide: 'current' | 'previous' | null;
  headline: string;
  currentRun: CrawlRunRef | null;
  previousRun: CrawlRunRef | null;
  metrics: CrawlRunMetricDelta[];
  issues: CrawlRunIssueComparison;
  notes: string[];
}

export interface UrlRunHistoryEntry {
  run: CrawlRunRef;
  snapshot: { analysisOk: boolean; analysisNote: string | null; capturedAt: string | null } | null;
}

export interface UrlRunComparisonPayload {
  urlId: string;
  url: string;
  scopeDomain: string | null;
  runsCaptured: number;
  history: UrlRunHistoryEntry[];
  comparison: CrawlRunComparison | null;
}

@Injectable({ providedIn: 'root' })
export class UrlRunComparisonService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  getForUrl(urlId: string): Observable<{ success: boolean; data: UrlRunComparisonPayload }> {
    return this.http.get<{ success: boolean; data: UrlRunComparisonPayload }>(
      `${this.API_URL}/urls/${urlId}/run-comparison`
    );
  }
}
